import { Card, CardContent } from '@crm/ui';
import { CalendarPlus, Clock, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { QuickActions } from '@/app/layouts/quick-actions';
import { useDocumentTitle } from '@/hooks';
import { formatDateTime } from '@/lib/utils';
import { CampaignsWidget } from '@/modules/campaigns';
import { type Consulta, consultaInicial, FunnelWidget } from '@/modules/leads';
import {
  DashboardFilters,
  type DashboardPeriod,
  DashboardWidgets,
  TimelineList,
  useDashboard,
  useKpis,
} from '@/modules/management';
import { OnboardingCard } from '@/modules/onboarding';
import { TodayQueue } from '@/modules/schedule';
import { useCan, useSession } from '../hooks';
import { roleLabel } from '../utils';

export function DashboardPage() {
  useDocumentTitle('Painel');

  const { user, session } = useSession();

  // Os indicadores da operacao so aparecem para quem tem o modulo de gestao.
  const canSeeReports = useCan('reports', 'READ');
  // A competencia e a unidade valem para a tela inteira: numeros, metas e
  // graficos olham o mesmo recorte.
  const agora = new Date();
  const [periodo, setPeriodo] = useState<DashboardPeriod>({
    year: agora.getFullYear(),
    month: agora.getMonth() + 1,
  });
  const [unitId, setUnitId] = useState('');
  // O recorte dos graficos vive aqui: funil e pipeline moram em boxes
  // separados, mas leem o mesmo filtro - dois filtros iguais sairiam de
  // sincronia na primeira troca.
  const [consulta, setConsulta] = useState<Consulta>(() => consultaInicial(periodo));

  const kpis = useKpis({
    from: new Date(periodo.year, periodo.month - 1, 1),
    to: new Date(periodo.year, periodo.month, 0, 23, 59, 59),
    unitId: unitId || undefined,
  });
  const dashboard = useDashboard({ unitId: unitId || undefined, ...periodo });

  return (
    <div className="space-y-8">
      {/* O guia vem antes de tudo, compacto - so o passo atual. Quem ja
          configurou nao ve nada aqui, ele some sozinho. */}
      <OnboardingCard />

      {/* No celular a navegacao vem antes dos numeros: quem abre o app no
          balcao quer ir para a agenda, nao ler o painel. */}
      <QuickActions />

      <DashboardFilters
        period={periodo}
        onPeriodChange={setPeriodo}
        unitId={unitId}
        onUnitChange={setUnitId}
      />

      {/* A operacao do mes: o numero que responde "como estamos indo". O funil
          entra logo antes dos graficos - de onde vieram os clientes do mes. */}
      {canSeeReports ? (
        <DashboardWidgets
          data={dashboard.data}
          kpis={kpis.data}
          period={periodo}
          onYearChange={(year) => setPeriodo((atual) => ({ ...atual, year }))}
          beforeCharts={
            <FunnelWidget
              consulta={consulta}
              onConsultaChange={setConsulta}
              period={periodo}
              unitId={unitId || undefined}
            >
              {/* O que esta no ar puxando gente para o funil, no rodape do
                  mesmo box: a campanha e a origem do que o grafico conta, e
                  em moldura propria parecia outro assunto. */}
              <CampaignsWidget />
            </FunnelWidget>
          }
        />
      ) : null}

      {/* Quem esta na fila fica a esquerda, o que ja aconteceu a direita. */}
      {/* `min-w-0`: item de grid nasce com largura minima do conteudo, e uma
          linha longa da timeline empurrava a lista para fora da tela. */}
      <div className="grid items-stretch gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <TodayQueue />
        {canSeeReports ? <TimelineList limit={8} /> : null}
      </div>

      {/* Sessao atual: uma faixa de rodape, nao um bloco de formulario. Sao
          quatro dados de conferencia - quem esta logado e ate quando -, e
          nenhum deles justifica meia tela. */}
      <Card variant="flush">
        <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 py-3 text-sm">
          {[
            { icon: Mail, label: user?.email },
            { icon: ShieldCheck, label: user ? roleLabel(user.role) : '--' },
            { icon: CalendarPlus, label: user ? `Desde ${formatDateTime(user.createdAt)}` : '--' },
            {
              icon: Clock,
              label: session ? `Sessao ate ${formatDateTime(session.expiresAt)}` : '--',
            },
          ].map((item) => (
            <span key={item.label} className="flex items-center gap-2 text-muted-foreground">
              <item.icon className="h-4 w-4 shrink-0" aria-hidden />
              {item.label}
            </span>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
